<script setup lang="ts">
/**
 * BoardMembersView — /board-members
 *
 * Shows the Cypress Ridge board roster with role badges, notification pref icons,
 * invite modal, edit drawer, and CSV export. Tabs: All | Board | Property management
 * | Pending invites | Roles & permissions (StubView).
 */
import { ref, onMounted, computed } from 'vue';
import BaseTabs from '@/components/ui/BaseTabs.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import StatCard from '@/components/ui/StatCard.vue';
import BaseCard from '@/components/ui/BaseCard.vue';
import MemberRow from '@/components/board/MemberRow.vue';
import InviteModal from '@/components/board/InviteModal.vue';
import MemberEditDrawer from '@/components/board/MemberEditDrawer.vue';
import { useBoardMembersStore } from '@/stores/boardMembers';
import type { MemberListItemDto, ActiveTab } from '@/stores/boardMembers';

const store = useBoardMembersStore();

// ── Tab config ────────────────────────────────────────────────────────────────

const TABS: { key: ActiveTab; label: string }[] = [
  { key: 'all', label: 'All members' },
  { key: 'board', label: 'Board' },
  { key: 'property_management', label: 'Property management' },
  { key: 'pending_invites', label: 'Pending invites' },
  { key: 'roles_permissions', label: 'Roles & permissions' },
];

const tabsWithCounts = computed(() =>
  TABS.map((t) => ({
    ...t,
    count:
      t.key === 'pending_invites' ? store.pendingInviteCount : undefined,
  })),
);

// ── UI state ──────────────────────────────────────────────────────────────────

const showInviteModal = ref(false);
const editingMember = ref<MemberListItemDto | null>(null);

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(async () => {
  await store.fetchMembers('all');
});

async function onTabChange(key: string) {
  await store.fetchMembers(key as ActiveTab);
}

function openEdit(id: string) {
  const m = store.members.find((m) => m.id === id);
  if (m) editingMember.value = m;
}

async function onExportCsv() {
  try {
    await store.exportCsv();
  } catch {
    // Non-fatal; will add toast in Notifications slice
  }
}
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="board-members-view">
    <!-- Page header -->
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="text-page text-fg font-semibold">Board members</h1>
        <p class="text-caption text-muted mt-0.5">
          <span class="font-mono tabular-nums">{{ store.activeCount }}</span> active
          <template v-if="store.pendingInviteCount > 0">
            · <span class="font-mono tabular-nums">{{ store.pendingInviteCount }}</span> pending invitation
          </template>
          · Manage roles, alert routing, and SMS preferences.
        </p>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <BaseButton variant="secondary" size="sm" @click="onExportCsv">
          <template #left>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
          </template>
          Export CSV
        </BaseButton>
        <BaseButton
          variant="primary"
          size="sm"
          data-testid="invite-member-btn"
          @click="showInviteModal = true"
        >
          <template #left>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="w-3.5 h-3.5">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <path d="M20 8v6M17 11h6" />
            </svg>
          </template>
          Invite member
        </BaseButton>
      </div>
    </div>

    <!-- Stat strip -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <StatCard
        label="Active members"
        :value="store.activeCount"
        :sub="store.pendingInviteCount > 0 ? `${store.pendingInviteCount} pending invite` : undefined"
        data-testid="board-active-count"
      />
      <StatCard
        label="SMS-enabled"
        :value="store.smsCount"
        :sub="`${store.activeCount - store.smsCount} email-only`"
        data-testid="board-sms-count"
      />
      <StatCard
        label="Active in last 7 days"
        :value="store.activeLastSevenDays"
        data-testid="board-active-7d"
      />
    </div>

    <!-- Tabs + table card -->
    <BaseCard padding="none">
      <!-- Tabs -->
      <div class="px-4 pt-3">
        <BaseTabs
          :tabs="tabsWithCounts"
          :active="store.activeTab"
          @change="onTabChange"
        />
      </div>

      <!-- Roles & permissions StubView -->
      <div
        v-if="store.activeTab === 'roles_permissions'"
        class="p-8 text-center text-muted text-body"
      >
        <p class="font-semibold text-fg-soft">Roles &amp; permissions</p>
        <p class="mt-1 text-caption">Coming in a future slice.</p>
      </div>

      <!-- Loading skeleton -->
      <div v-else-if="store.isLoading" class="p-4 flex flex-col gap-3">
        <div v-for="i in 5" :key="i" class="h-12 bg-canvas-soft rounded animate-pulse" />
      </div>

      <!-- Error state -->
      <div v-else-if="store.error" class="p-4 text-center text-red-700 text-body">
        {{ store.error }}
      </div>

      <template v-else>
        <!-- Pending invites tab -->
        <template v-if="store.activeTab === 'pending_invites'">
          <div v-if="store.invitations.length === 0" class="p-8 text-center text-muted text-body">
            No pending invitations.
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full min-w-[640px]">
              <thead class="bg-canvas-soft border-b border-border">
                <tr>
                  <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Member</th>
                  <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Role</th>
                  <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Status</th>
                  <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Alert categories</th>
                  <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Expires</th>
                  <th class="px-4 py-2.5 text-right text-caption font-semibold text-muted-2 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-border">
                <MemberRow
                  v-for="inv in store.invitations"
                  :key="inv.id"
                  :invitation="inv"
                  @resend="store.resendInvite"
                  @revoke="store.revokeInvite"
                />
              </tbody>
            </table>
          </div>
          <div class="flex items-center justify-between px-4 py-3 border-t border-border">
            <span class="text-caption text-muted font-mono tabular-nums">
              {{ store.invitations.length }} pending invitation{{ store.invitations.length !== 1 ? 's' : '' }}
            </span>
          </div>
        </template>

        <!-- Members tabs (all / board / property_management) -->
        <template v-else>
          <div v-if="store.members.length === 0" class="p-8 text-center text-muted text-body">
            No members found.
          </div>
          <template v-else>
            <!-- Desktop table (hidden below md) -->
            <div class="hidden md:block overflow-x-auto">
              <table class="w-full min-w-[720px]">
                <thead class="bg-canvas-soft border-b border-border">
                  <tr>
                    <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Member</th>
                    <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Role</th>
                    <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Notifications</th>
                    <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Alert categories</th>
                    <th class="px-4 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Last active</th>
                    <th class="px-4 py-2.5 text-right text-caption font-semibold text-muted-2 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-border">
                  <MemberRow
                    v-for="m in store.members"
                    :key="m.id"
                    :member="m"
                    @edit="openEdit"
                  />
                </tbody>
              </table>
            </div>

            <!-- Mobile card stack (shown below md) -->
            <div class="md:hidden flex flex-col divide-y divide-border">
              <div
                v-for="m in store.members"
                :key="m.id"
                data-testid="member-row"
                class="px-4 py-3 flex items-center gap-3"
              >
                <div class="w-9 h-9 rounded-pill bg-navy-100 text-navy-700 grid place-items-center font-semibold text-caption flex-shrink-0">
                  {{ m.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-body font-semibold text-fg truncate">{{ m.name }}</div>
                  <div class="text-caption text-muted font-mono tabular-nums truncate">{{ m.email }}</div>
                </div>
                <button
                  class="h-8 px-2.5 text-caption text-muted hover:text-fg border border-border rounded-md hover:bg-canvas-soft transition-colors flex-shrink-0"
                  @click="openEdit(m.id)"
                >
                  Edit
                </button>
              </div>
            </div>
          </template>

          <!-- Footer -->
          <div class="flex items-center justify-between px-4 py-3 border-t border-border">
            <span class="text-caption text-muted font-mono tabular-nums">
              {{ store.members.length }} member{{ store.members.length !== 1 ? 's' : '' }}
            </span>
          </div>
        </template>
      </template>
    </BaseCard>
  </div>

  <!-- Invite modal -->
  <InviteModal
    v-if="showInviteModal"
    @close="showInviteModal = false"
  />

  <!-- Edit drawer -->
  <MemberEditDrawer
    v-if="editingMember"
    :member="editingMember"
    @close="editingMember = null"
    @updated="store.fetchMembers()"
  />
</template>
